import React, { useState, useEffect, useRef } from 'react';
import { pomodoroService } from '../services/api';
import { 
  Clock, 
  Settings, 
  Minimize2, 
  ArrowLeft, 
  Save, 
  Play, 
  Pause, 
  CloudRain, 
  Wind 
} from 'react-feather';
import { useTheme } from '../ThemeContext';
import './PomodoroTimer.css';

// --- Sound File Paths --- 
// Use paths relative to the public directory
const RAIN_SOUND_URL = '/sounds/rain.mp3'; 
const FOREST_SOUND_URL = '/sounds/forest.mp3';
// ------------------------

const PomodoroTimer = () => {
  // Timer states
  const [isRunning, setIsRunning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(25 * 60); // Default: 25 minutes in seconds
  const [isBreak, setIsBreak] = useState(false);
  const [sessionCount, setSessionCount] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [isSetting, setIsSetting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Theme context
  const { theme } = useTheme();
  
  // Ambient Sound States
  const [ambientSound, setAmbientSound] = useState(null); // 'rain', 'forest', or null
  const [isSoundPlaying, setIsSoundPlaying] = useState(false);
  
  // Settings
  const [settings, setSettings] = useState({
    focusDuration: 25,
    shortBreakDuration: 5,
    longBreakDuration: 15,
    sessionsBeforeLongBreak: 4
  });
  
  // Reference for the timer interval
  const timerRef = useRef(null);
  // Reference for the audio element
  const audioRef = useRef(null);
  
  // Add temporary settings state to store original values
  const [tempSettings, setTempSettings] = useState({...settings});
  
  // Initialize state and fetch settings from backend on component mount
  useEffect(() => {
    const initializeTimer = async () => {
      setIsLoading(true);
      
      try {
        // Try to get settings from backend first
        const savedSettings = await pomodoroService.getSettings();
        if (savedSettings) {
          setSettings({
            focusDuration: savedSettings.focusDuration,
            shortBreakDuration: savedSettings.shortBreakDuration,
            longBreakDuration: savedSettings.longBreakDuration,
            sessionsBeforeLongBreak: savedSettings.sessionsBeforeLongBreak
          });
          
          // Set initial time based on focus duration from backend
          setTimeLeft(savedSettings.focusDuration * 60);
        }
      } catch (error) {
        console.error('Failed to load settings from server. Using defaults.', error);
        // If backend call fails, try localStorage as fallback
        const savedSettingsStr = localStorage.getItem('pomodoroSettings');
        if (savedSettingsStr) {
          const localSettings = JSON.parse(savedSettingsStr);
          setSettings(localSettings);
          setTimeLeft(localSettings.focusDuration * 60);
        }
      }
      
      // Load timer state from localStorage (session persistence)
      const savedStateStr = localStorage.getItem('pomodoroState');
      const savedVisibleStr = localStorage.getItem('pomodoroVisible');
      
      if (savedStateStr) {
        const state = JSON.parse(savedStateStr);
        setIsRunning(state.isRunning);
        setTimeLeft(state.timeLeft);
        setIsBreak(state.isBreak);
        setSessionCount(state.sessionCount);
        
        // Calculate if timer should still be running based on saved timestamp
        if (state.isRunning && state.timestamp) {
          const elapsedSeconds = Math.floor((Date.now() - state.timestamp) / 1000);
          if (elapsedSeconds < state.timeLeft) {
            setTimeLeft(state.timeLeft - elapsedSeconds);
          } else {
            // Time would have expired, move to next phase
            handleTimerComplete();
          }
        }
      }
      
      if (savedVisibleStr !== null) {
        setIsVisible(JSON.parse(savedVisibleStr));
      }
      
      // Load ambient sound state from localStorage
      const savedAmbientSound = localStorage.getItem('pomodoroAmbientSound');
      const savedSoundPlaying = localStorage.getItem('pomodoroSoundPlaying');
      if (savedAmbientSound) {
        setAmbientSound(savedAmbientSound);
      }
      if (savedSoundPlaying) {
        // Don't auto-play on load, just restore the state
        // setIsSoundPlaying(JSON.parse(savedSoundPlaying));
      }
      
      setIsLoading(false);
    };
    
    initializeTimer();
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      // Cleanup audio element on unmount
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);
  
  // Save current state to localStorage when it changes
  useEffect(() => {
    // Only save if the component has mounted (to avoid overwriting saved data on initial render)
    if (!isLoading) {
      localStorage.setItem('pomodoroState', JSON.stringify({
        isRunning,
        timeLeft,
        isBreak,
        sessionCount,
        timestamp: Date.now()
      }));
    }
  }, [isRunning, timeLeft, isBreak, sessionCount, isLoading]);
  
  // Save visibility state when it changes
  useEffect(() => {
    if (!isLoading) {
      localStorage.setItem('pomodoroVisible', JSON.stringify(isVisible));
    }
  }, [isVisible, isLoading]);
  
  // Save ambient sound state
  useEffect(() => {
    if (!isLoading) {
      localStorage.setItem('pomodoroAmbientSound', ambientSound || '');
      localStorage.setItem('pomodoroSoundPlaying', JSON.stringify(isSoundPlaying));
    }
  }, [ambientSound, isSoundPlaying, isLoading]);
  
  // Effect to control audio playback
  useEffect(() => {
    if (!audioRef.current) return; // Make sure audio element exists

    if (isSoundPlaying && ambientSound) {
      const soundUrl = ambientSound === 'rain' ? RAIN_SOUND_URL : FOREST_SOUND_URL;
      // Only set src if it's different or audio is not playing
      if (audioRef.current.src !== soundUrl || audioRef.current.paused) {
        audioRef.current.src = soundUrl;
        audioRef.current.load(); // Important for changing src
        audioRef.current.play().catch(error => console.error("Audio play failed:", error));
      }
    } else {
      audioRef.current.pause();
    }
    
    // Ensure looping
    audioRef.current.loop = true;

  }, [isSoundPlaying, ambientSound]);
  
  // Timer logic
  useEffect(() => {
    if (isRunning) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleTimerComplete();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRunning]);
  
  // Format seconds to MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Handle timer completion
  const handleTimerComplete = async () => {
    // Use browser notification API instead of audio file
    if (Notification.permission === 'granted') {
      const notification = new Notification(isBreak ? 'Break Time Complete!' : 'Focus Time Complete!', {
        body: isBreak ? 'Time to focus again!' : 'Great job! Take a break now.',
        icon: '/logo.svg'
      });
      
      // Auto close after 5 seconds
      setTimeout(() => notification.close(), 5000);
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission();
    }
    
    // Play a clock ticking sound using the Web Audio API
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Create oscillator for tick sound
      const tick = () => {
        const osc = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        osc.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        osc.type = 'sine';
        osc.frequency.value = 1000; // Higher frequency for clock tick
        
        // Short duration with quick fade out for "tick" sound
        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.1);
        
        osc.start();
        osc.stop(audioContext.currentTime + 0.1);
      };
      
      // Play 3 quick ticks in succession
      tick();
      setTimeout(() => tick(), 150);
      setTimeout(() => tick(), 300);
    } catch (e) {
      console.error('Failed to play notification sound:', e);
    }
    
    if (!isBreak) {
      // Focus session completed
      const newSessionCount = sessionCount + 1;
      setSessionCount(newSessionCount);
      
      // Report completed session to backend
      try {
        await pomodoroService.createSession(
          new Date().toISOString(),
          settings.focusDuration * 60
        );
        
        // Dispatch a custom event to notify that a pomodoro session was completed
        window.dispatchEvent(new CustomEvent('pomodoro-session-completed'));
      } catch (error) {
        console.error('Failed to record pomodoro session:', error);
      }
      
      // Determine if it's time for a short or long break
      const isLongBreakTime = newSessionCount % settings.sessionsBeforeLongBreak === 0;
      const breakDuration = isLongBreakTime 
        ? settings.longBreakDuration 
        : settings.shortBreakDuration;
      
      setIsBreak(true);
      setTimeLeft(breakDuration * 60);
    } else {
      // Break completed, back to focus
      setIsBreak(false);
      setTimeLeft(settings.focusDuration * 60);
    }
    
    // Auto-continue if already running
    setIsRunning(true);
  };
  
  // Control functions
  const toggleTimer = () => {
    setIsRunning(!isRunning);
  };
  
  const resetTimer = () => {
    clearInterval(timerRef.current);
    setIsRunning(false);
    setIsBreak(false);
    setTimeLeft(settings.focusDuration * 60);
  };
  
  const skipToNext = () => {
    clearInterval(timerRef.current);
    setIsRunning(false);
    handleTimerComplete();
  };
  
  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };
  
  const toggleSettings = () => {
    // When opening settings, store the current settings to restore if user cancels
    if (!isSetting) {
      setTempSettings({...settings});
    }
    setIsSetting(!isSetting);
  };
  
  const handleSettingChange = (field, value) => {
    const numericValue = parseInt(value, 10);
    if (isNaN(numericValue) || numericValue < 1) return;
    
    setSettings({
      ...settings,
      [field]: numericValue
    });
    
    // If we're editing the focus duration and not currently in a break,
    // update the current timer
    if (field === 'focusDuration' && !isBreak) {
      setTimeLeft(numericValue * 60);
    }
  };
  
  // Add a function to handle going back without saving
  const handleBackFromSettings = () => {
    // Restore original settings
    setSettings({...tempSettings});
    setIsSetting(false);
  };
  
  const saveSettings = async () => {
    setIsSetting(false);
    
    try {
      // Save settings to backend
      await pomodoroService.saveSettings(settings);
      
      // Also save to localStorage as backup
      localStorage.setItem('pomodoroSettings', JSON.stringify(settings));
    } catch (error) {
      console.error('Failed to save settings to server, using localStorage only:', error);
      localStorage.setItem('pomodoroSettings', JSON.stringify(settings));
    }
    
    // If we're in focus mode, reset the timer with new duration
    if (!isBreak) {
      setTimeLeft(settings.focusDuration * 60);
    } else {
      // If we're in a break, adjust the time based on the break type
      const isLongBreak = sessionCount % settings.sessionsBeforeLongBreak === 0;
      setTimeLeft((isLongBreak ? settings.longBreakDuration : settings.shortBreakDuration) * 60);
    }
  };
  
  // --- Ambient Sound Controls ---
  const selectAmbientSound = (soundType) => {
    if (ambientSound === soundType) {
      // If clicking the same sound, toggle play/pause
      setIsSoundPlaying(!isSoundPlaying);
    } else {
      // Switch sound and start playing
      setAmbientSound(soundType);
      setIsSoundPlaying(true);
    }
  };

  const toggleSoundPlayPause = () => {
    if (!ambientSound) return; // Do nothing if no sound selected
    setIsSoundPlaying(!isSoundPlaying);
  };
  
  if (isLoading) {
    return (
      <div className={`pomodoro-container visible ${theme}`}>
        <div className="pomodoro-loading">Loading timer...</div>
      </div>
    );
  }
  
  return (
    <div className={`pomodoro-container ${isVisible ? 'visible' : 'collapsed'} ${theme}`}>
      {isVisible ? (
        <>
          <div className="pomodoro-header">
            <span>{isBreak ? 'Break Time' : 'Focus Time'}</span>
            <div className="pomodoro-controls">
              <button onClick={toggleSettings} className="setting-button">
                <Settings size={16} />
              </button>
              <button onClick={toggleVisibility} className="minimize-button">
                <Minimize2 size={16} />
              </button>
            </div>
          </div>
          
          {isSetting ? (
            <div className="pomodoro-settings">
              <h4 style={{color: theme === 'light' ? '#333' : '#0a101e'}}>Timer Settings</h4>
              <div className="setting-group">
                <label>Focus Duration (minutes):</label>
                <input 
                  type="number" 
                  min="1" 
                  max="60" 
                  value={settings.focusDuration} 
                  onChange={(e) => handleSettingChange('focusDuration', e.target.value)}
                />
              </div>
              <div className="setting-group">
                <label>Short Break (minutes):</label>
                <input 
                  type="number" 
                  min="1" 
                  max="30" 
                  value={settings.shortBreakDuration} 
                  onChange={(e) => handleSettingChange('shortBreakDuration', e.target.value)}
                />
              </div>
              <div className="setting-group">
                <label>Long Break (minutes):</label>
                <input 
                  type="number" 
                  min="1" 
                  max="60" 
                  value={settings.longBreakDuration} 
                  onChange={(e) => handleSettingChange('longBreakDuration', e.target.value)}
                />
              </div>
              <div className="setting-group">
                <label>Sessions before long break:</label>
                <input 
                  type="number" 
                  min="1" 
                  max="10" 
                  value={settings.sessionsBeforeLongBreak} 
                  onChange={(e) => handleSettingChange('sessionsBeforeLongBreak', e.target.value)}
                />
              </div>
              <div className="settings-buttons">
                <button onClick={handleBackFromSettings} className="back-button">
                  <ArrowLeft size={16} /> Back
                </button>
                <button onClick={saveSettings} className="save-settings-button">
                  Save
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="pomodoro-display">
                <div className={`timer-display ${isRunning ? 'ticking' : ''}`}>
                  {formatTime(timeLeft)}
                </div>
              </div>
              
              {/* Ambient Sound Controls - Only show when not in settings */}
              <div className="ambient-sound-controls">
                <button 
                  onClick={() => selectAmbientSound('rain')} 
                  className={`sound-button ${ambientSound === 'rain' ? 'active' : ''}`}
                  title="Rain Sound"
                >
                  <CloudRain size={18} />
                </button>
                <button 
                  onClick={() => selectAmbientSound('forest')} 
                  className={`sound-button ${ambientSound === 'forest' ? 'active' : ''}`}
                  title="Forest Sound"
                >
                  <Wind size={18} />
                </button>
                <button 
                  onClick={toggleSoundPlayPause} 
                  className={`sound-button play-pause-button ${!ambientSound ? 'disabled' : ''}`}
                  disabled={!ambientSound} 
                  title={isSoundPlaying ? "Pause Sound" : "Play Sound"}
                >
                  {isSoundPlaying ? <Pause size={18} /> : <Play size={18} />}
                </button>
              </div>
              
              <div className="pomodoro-buttons">
                <button onClick={toggleTimer} className="timer-button">
                  {isRunning ? 'Pause' : 'Start'}
                </button>
                <button onClick={resetTimer} className="timer-button">Reset</button>
                {isBreak && (
                  <button onClick={skipToNext} className="timer-button">Skip</button>
                )}
              </div>
            </>
          )}
        </>
      ) : (
        <div className="pomodoro-collapsed" onClick={toggleVisibility}>
          <Clock size={20} className="timer-icon" />
          <span className="collapsed-timer">{formatTime(timeLeft)}</span>
        </div>
      )}
      {/* Audio element for ambient sounds */}
      <audio ref={audioRef} loop></audio>
    </div>
  );
};

export default PomodoroTimer; 